/**
 * As pastilhas de etiqueta — usadas em três telas.
 *
 * ── Por que aqui, e não em `atendimento/` ─────────────────────────────────
 *
 * Porque a mesma pastilha aparece no cabeçalho da conversa, na fila e no
 * Cliente 360. Ela nasceu dentro de `atendimento/conversa.tsx`, e o Cliente
 * 360 importando de lá acoplaria duas telas por um detalhe visual — a primeira
 * refatoração de uma quebraria a outra sem nenhum aviso.
 *
 * O estilo veio junto, para `shared/estilo/base.css`, pelo mesmo motivo: em
 * `atendimento.css` ele só existia para quem importasse aquela tela, e a
 * pastilha do 360 sairia sem cor nenhuma.
 *
 * ── A COR vem do catálogo, e não da conversa ──────────────────────────────
 *
 * Junta, trocar a cor de uma etiqueta exigiria reprojetar toda conversa que a
 * tem — e até lá a mesma etiqueta sairia de duas cores em linhas vizinhas da
 * mesma fila. Enquanto o catálogo não chega, a pastilha sai em cor neutra: o
 * título a tela já tem, e é ele que informa.
 */

import type { ReactNode } from 'react';
import { useCatalogoDeEtiquetas } from '@/shared/api/consultas';
import { Etiquetas as EtiquetasDoSistema } from '@/shared/ui';

/*
 * A casca de DADOS: esta função sabe do catálogo da revenda — qual cor cada
 * etiqueta tem — e o componente compartilhado só sabe desenhar. Separar os
 * dois é o que deixa o componente ser usado no mostruário sem chamar a API, e
 * o que deixa esta casca trocar a fonte das cores sem mexer em desenho.
 */
export function Etiquetas({
  titulos,
  limite,
  emLinha,
  miuda,
}: {
  titulos: readonly string[];
  limite?: number;
  /** Sem quebra de linha: para linha de lista de altura fixa. */
  emLinha?: boolean;
  /** Um degrau menor. O padrão do componente acompanha `emLinha`. */
  miuda?: boolean;
}): ReactNode {
  const { data } = useCatalogoDeEtiquetas();
  if (titulos.length === 0) return null;

  const cores = new Map((data?.itens ?? []).map((e) => [e.titulo, e.cor]));
  return (
    <EtiquetasDoSistema
      itens={titulos.map((titulo) => ({ nome: titulo, cor: cores.get(titulo) ?? null }))}
      limite={limite}
      emLinha={emLinha}
      {...(miuda === undefined ? {} : { miuda })}
    />
  );
}
